import api from '../libs/api';
import env from '../env';

const assistant = {
  getSyncMeta: () => api().get('team-assistant/sync-meta'),
  getConversationFilters: () => api().get('team-assistant/conversation-filters'),
  getMessages: (params) => api().get('team-assistant/messages', { params }),
  getKnowledgeOverview: () => api().get('team-assistant/knowledge-overview'),
  listDocuments: (params) => api().get('team-assistant/documents', { params }),
  deleteDocument: (id) => api().delete(`team-assistant/documents/${id}`),
  fetchDocumentFile: async (id, { inline = false } = {}) => {
    const token = localStorage.getItem('token');
    const qs = inline ? '?inline=1' : '';
    const response = await fetch(`${env.API_URL}/team-assistant/documents/${id}/file${qs}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        token: token || '',
      },
    });

    if (!response.ok) {
      let errMsg = 'Could not load file';
      try {
        const json = await response.json();
        errMsg = json.message || errMsg;
      } catch (_) {
        /* ignore */
      }
      throw new Error(errMsg);
    }

    return response.blob();
  },
  searchKnowledge: (params) => api().get('team-assistant/search', { params }),
  uploadDocument: (file, onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api().post('team-assistant/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
  confirmCatalogAction: (actionId) =>
    api().post(`team-assistant/actions/${encodeURIComponent(actionId)}/confirm`),
  listMemories: (params) => api().get('team-assistant/memories', { params }),
  createMemory: (body) => api().post('team-assistant/memories', body),
  updateMemory: (id, body) => api().put(`team-assistant/memories/${id}`, body),
  confirmMemory: (id) => api().post(`team-assistant/memories/${id}/confirm`),
  archiveMemory: (id) => api().post(`team-assistant/memories/${id}/archive`),
  unarchiveMemory: (id) => api().post(`team-assistant/memories/${id}/unarchive`),
  deleteMemory: (id) => api().delete(`team-assistant/memories/${id}`),
  streamChat: async (
    message,
    {
      documentId,
      documentIds,
      useWebSearch,
      regenerateAssistantMessageId,
      signal,
      onToken,
      onAction,
      onDone,
      onError,
    }
  ) => {
    const token = localStorage.getItem('token');
    const ids =
      documentIds?.length > 0
        ? documentIds
        : documentId
          ? [documentId]
          : undefined;
    let response;
    try {
      response = await fetch(`${env.API_URL}/team-assistant/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          token: token || '',
        },
        body: JSON.stringify({
          message: regenerateAssistantMessageId ? undefined : message,
          regenerate_assistant_message_id: regenerateAssistantMessageId || undefined,
          document_ids: regenerateAssistantMessageId ? undefined : ids,
          document_id: regenerateAssistantMessageId ? undefined : documentId || undefined,
          use_web_search: Boolean(useWebSearch),
        }),
        signal,
      });
    } catch (e) {
      if (e?.name === 'AbortError') {
        onError?.(e);
        return;
      }
      const isNetwork =
        e instanceof TypeError ||
        e?.message === 'Failed to fetch' ||
        e?.name === 'TypeError';
      const hint = isNetwork
        ? `Cannot reach the API at ${env.API_URL}. Start the Laravel backend (php artisan serve) or check frontend env (currently "${env.flag}").`
        : e?.message || 'Chat request failed';
      onError?.(new Error(hint));
      return;
    }

    if (!response.ok) {
      let errMsg = 'Chat request failed';
      try {
        const json = await response.json();
        errMsg = json.message || errMsg;
      } catch (_) {
        /* ignore */
      }
      onError?.(new Error(errMsg));
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5).trim();
        if (payload === '[DONE]') {
          await Promise.resolve(onDone?.());
          return;
        }
        try {
          const parsed = JSON.parse(payload);
          if (parsed.error) {
            onError?.(new Error(parsed.error));
            return;
          }
          if (parsed.action) onAction?.(parsed.action);
          if (parsed.text) onToken?.(parsed.text);
        } catch (_) {
          /* ignore partial json */
        }
      }
    }
    await Promise.resolve(onDone?.());
  },
};

export default assistant;
